import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { 
  UserPlus, 
  Search, 
  Calendar as CalendarIcon, 
  Clock, 
  FileText,
  Video,
  User,
  X,
  ChevronDown,
  Check,
  AlertCircle,
  AlertTriangle,
  Loader2,
  Plus,
  Upload,
  Phone
} from 'lucide-react';
import { supabase } from '../supabaseClient';
import { useNotifications } from '../context/NotificationContext';
import './AppointmentModal.css';

const defaultInsurances = [
  { name: 'PARTICULAR', plans: ['-'] },
  { name: 'A B G', plans: ['Plan 210', 'Plan 310'] },
  { name: 'OSDE', plans: ['Binario', '210', '310', '410', '450', '510'] },
  { name: 'SWISS MEDICAL', plans: ['Nubial', 'SMG02', 'SMG20', 'SMG40', 'SMG50'] },
  { name: 'GALENO', plans: ['Blanco', 'Azul', 'Plata', 'Oro'] },
  { name: 'MEDICUS', plans: ['Azul', 'Celeste', 'Family'] },
  { name: 'OMINT', plans: ['O', 'F', 'S', 'Premium'] },
  { name: 'MEDIFE', plans: ['Bronce', 'Plata', 'Oro', 'Platinum'] },
  { name: 'SANCOR SALUD', plans: ['1000', '2000', '3000', '4000'] },
  { name: 'ACCORD SALUD', plans: ['Accord 110', 'Accord 210', 'Accord 310'] },
  { name: 'PAMI', plans: ['General', 'Especialista'] },
  { name: 'IOMA', plans: ['General', 'Accesorios'] },
  { name: 'UNIóN PERSONAL', plans: ['Classic', 'Familiar', 'Accord'] },
  { name: 'PREVENCION SALUD', plans: ['A1', 'A2', 'A3', 'A4'] },
  { name: 'HOMIC', plans: ['H1', 'H2', 'H3'] },
  { name: 'OSECAC', plans: ['General'] }
];

const getInsurances = () => {
  try {
    const saved = localStorage.getItem('gdt_insurances');
    const parsed = saved ? JSON.parse(saved) : null;
    const list = (parsed && Array.isArray(parsed)) ? parsed : [...defaultInsurances];
    if (!list.find(i => i.name === 'PARTICULAR')) list.unshift({ name: 'PARTICULAR', plans: ['-'] });
    return list.filter(i => i.active !== false);
  } catch(e) { return defaultInsurances; }
};

const AppointmentModal = ({ isOpen, onClose, initialData, onSave, professionalsList = [] }) => {
  const insurancesList = React.useMemo(() => getInsurances(), [isOpen]);
  const { addNotification } = useNotifications();
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  // Load active staff
  const activeStaff = JSON.parse(localStorage.getItem('gdt_active_staff') || '{}');
  const staffName = activeStaff.full_name || '';

  const initialProfessional = initialData?.professional || (activeStaff.type === 'Profesional' ? staffName : null) || professionalsList?.filter(p => p.type === 'Profesional')?.[0]?.full_name || '';
  const initialSpecialty = initialData?.specialty || professionalsList?.find(p => p.full_name === initialProfessional)?.specialty || 'CONSULTA GENERAL';

  const [formData, setFormData] = useState({
    id: initialData?.id || null,
    patient_id: initialData?.patient_id || null,
    patient_dni: initialData?.patient_dni || '',
    studyType: initialData?.studyType || initialData?.study_type || 'CONSULTA GENERAL',
    patient: initialData?.patient || initialData?.patient_name || '',
    date: initialData?.date || initialData?.appointment_date || '',
    time: initialData?.time || initialData?.appointment_time || '',
    insurance: initialData?.insurance || 'PARTICULAR',
    plan: initialData?.plan || '-',
    professional: initialProfessional,
    specialty: initialSpecialty,
    type: 'presencial',
    notes: initialData?.notes || initialData?.admin_notes || ''
  });

  const [attachedFiles, setAttachedFiles] = useState(initialData?.study_details?.attached_files || []);
  const [isUploading, setIsUploading] = useState(false);

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setIsUploading(true);
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
      
      const { error: uploadError } = await supabase.storage.from('patient-docs').upload(fileName, file);
      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage.from('patient-docs').getPublicUrl(fileName);
      
      setAttachedFiles(prev => [...prev, { name: file.name, url: publicUrl, study_name: 'Adicional' }]);
      addNotification({ title: 'Éxito', message: 'Archivo adjuntado correctamente', type: 'success' });
    } catch (err) {
      console.error(err);
      addNotification({ title: 'Error', message: 'No se pudo subir el archivo', type: 'error' });
    } finally {
      setIsUploading(false);
    }
  };

  const [activeTab, setActiveTab] = useState('datos');
  const [patientSearch, setPatientSearch] = useState(initialData?.patient || '');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);

  const currentPlans = React.useMemo(() => {
    const ins = insurancesList.find(i => i.name === formData.insurance);
    return ins?.plans || ['-'];
  }, [formData.insurance, insurancesList]);

  // New patient state
  const [showCreatePatient, setShowCreatePatient] = useState(false);
  const [showNewPatientResults, setShowNewPatientResults] = useState(false);
  const [newPatientSearchResults, setNewPatientSearchResults] = useState([]);
  const [newPatient, setNewPatient] = useState({
    full_name: '',
    dni: '',
    phone: '',
    email: '',
    insurance_provider: 'PARTICULAR',
    insurance_plan: '-',
    affiliate_number: ''
  });

  const newPatientPlans = React.useMemo(() => {
    const ins = insurancesList.find(i => i.name === newPatient.insurance_provider);
    return ins?.plans || ['-'];
  }, [newPatient.insurance_provider, insurancesList]);

  const [isSavingPatient, setIsSavingPatient] = useState(false);

  useEffect(() => {
    if (isOpen) {
      Promise.resolve().then(() => {
        setFormData({
          id: initialData?.id || null,
          patient_id: initialData?.patient_id || null,
          patient_dni: initialData?.patient_dni || '',
          studyType: initialData?.studyType || 'CONSULTA GENERAL',
          patient: initialData?.patient || '',
          date: initialData?.date || '',
          time: initialData?.time || '',
          insurance: initialData?.insurance || 'PARTICULAR',
          plan: initialData?.plan || '-',
          professional: initialData?.professional || (activeStaff.type === 'Profesional' ? staffName : null) || professionalsList?.filter(p => p.type === 'Profesional')?.[0]?.full_name || '',
          specialty: initialData?.specialty || initialSpecialty,
          type: 'presencial',
          notes: initialData?.notes || ''
        });
        setPatientSearch(initialData?.patient || '');
        setActiveTab('datos');
        setShowCreatePatient(false);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialData]);

  const [conflictAppointment, setConflictAppointment] = useState(null);

  useEffect(() => {
    if (!isOpen || !formData.date || !formData.time || (!formData.patient && !formData.patient_dni)) {
      setConflictAppointment(null);
      return;
    }

    const checkPatientConflict = async () => {
      try {
        const normName = (formData.patient || '').replace(/[\s\u00a0]+/g, ' ').trim();
        const normDni = (formData.patient_dni || '').replace(/[^0-9]/g, '').trim();

        const formattedTime = formData.time.length === 5 ? `${formData.time}:00` : formData.time;

        let query = supabase.from('appointments')
          .select('id, professional_name, appointment_time, appointment_date, study_type, patient_name, patient_dni, status')
          .eq('appointment_date', formData.date)
          .eq('appointment_time', formattedTime)
          .neq('status', 'cancelado');

        if (formData.id) {
          query = query.neq('id', formData.id);
        }

        const { data, error } = await query;
        if (error || !data || data.length === 0) {
          setConflictAppointment(null);
          return;
        }

        const match = data.find(app => {
          const appDni = (app.patient_dni || '').replace(/[^0-9]/g, '').trim();
          const appName = (app.patient_name || '').replace(/[\s\u00a0]+/g, ' ').trim().toLowerCase();
          
          if (normDni && appDni && normDni === appDni) return true;
          if (normName && appName && normName.toLowerCase() === appName) return true;
          return false;
        });

        setConflictAppointment(match || null);
      } catch (err) {
        console.error("Error checking patient appointment conflict:", err);
      }
    };

    const timer = setTimeout(checkPatientConflict, 300);
    return () => clearTimeout(timer);
  }, [isOpen, formData.date, formData.time, formData.patient, formData.patient_dni, formData.id]);

  if (!isOpen) return null;

  const searchNewPatients = async (query) => {
    if (query.length < 2) {
      setNewPatientSearchResults([]);
      setShowNewPatientResults(false);
      return;
    }
    setShowNewPatientResults(true);
    try {
      let queryBuilder = supabase.from('patients').select('*');
      const terms = query.trim().split(/\s+/);
      const andFullName = `and(${terms.map(t => `full_name.ilike.%${t}%`).join(',')})`;
      const cleanQuery = query.replace(/[^0-9a-zA-Z]/g, '');
      const wildcardDni = cleanQuery.length > 0 ? cleanQuery.split('').join('%') : query;
      const { data, error } = await queryBuilder
        .or(`${andFullName},dni.ilike.%${query}%,dni.ilike.%${wildcardDni}%`)
        .limit(5);
      if (error) throw error;
      setNewPatientSearchResults(data || []);
    } catch (err) {
      console.error('Search error:', err);
    }
  };

  const searchPatients = async (query) => {
    setPatientSearch(query);
    if (query.length < 2) {
      setSearchResults([]);
      setShowResults(false);
      return;
    }

    setIsSearching(true);
    setShowResults(true);
    try {
      let queryBuilder = supabase.from('patients').select('*');
      
      const terms = query.trim().split(/\s+/);
      const andFullName = `and(${terms.map(t => `full_name.ilike.%${t}%`).join(',')})`;
      
      const cleanQuery = query.replace(/[^0-9a-zA-Z]/g, '');
      const wildcardDni = cleanQuery.length > 0 ? cleanQuery.split('').join('%') : query;

      const { data, error } = await queryBuilder
        .or(`${andFullName},dni.ilike.%${query}%,dni.ilike.%${wildcardDni}%`)
        .limit(5);

      if (error) throw error;
      setSearchResults(data || []);
    } catch (err) {
      console.error('Search error:', err);
    } finally {
      setIsSearching(false);
    }
  };

  const selectPatient = (p) => {
    const fullName = p.full_name;
    setPatientSearch(fullName);
    setFormData({
      ...formData,
      patient: fullName,
      patient_id: p.id,
      patient_dni: p.dni,
      patient_email: p.email || '',
      patient_phone: p.phone || '',
      insurance: p.insurance_provider || 'PARTICULAR',
      plan: p.insurance_plan || '-'
    });
    setShowResults(false);
  };

  const handleSaveNewPatient = async (e) => {
    e.preventDefault();
    if (!newPatient.full_name.trim() || !newPatient.dni.trim() || !newPatient.phone.trim()) {
      addNotification('Nombre completo, DNI y Teléfono son requeridos', 'error');
      return;
    }

    setIsSavingPatient(true);
    try {
      if (newPatient.dni.trim()) {
        const { data: existing } = await supabase
          .from('patients')
          .select('id, full_name')
          .eq('dni', newPatient.dni.trim())
          .maybeSingle();
          
        if (existing) {
          addNotification(`Ya existe un paciente registrado con este DNI: ${existing.full_name}`, 'error');
          setIsSavingPatient(false);
          return;
        }
      }

      const { data, error } = await supabase
        .from('patients')
        .insert([{
          full_name: newPatient.full_name.trim().toUpperCase(),
          dni: newPatient.dni.trim() || null,
          phone: newPatient.phone.trim() || null,
          email: newPatient.email.trim() || null,
          insurance_provider: newPatient.insurance_provider,
          insurance_plan: newPatient.insurance_plan === '-' ? null : newPatient.insurance_plan,
          affiliate_number: newPatient.insurance_provider !== 'PARTICULAR' ? newPatient.affiliate_number.trim() || null : null
        }])
        .select();

      if (error) throw error;

      if (data && data.length > 0) {
        const createdPatient = data[0];
        selectPatient(createdPatient);
        addNotification('Paciente registrado y seleccionado con éxito', 'success');
      } else {
        // Fallback
        setPatientSearch(newPatient.full_name.trim().toUpperCase());
        setFormData({
          ...formData,
          patient: newPatient.full_name.trim().toUpperCase(),
          patient_email: newPatient.email.trim() || '',
          patient_phone: newPatient.phone.trim() || '',
          insurance: newPatient.insurance_provider,
          plan: newPatient.insurance_plan
        });
        addNotification('Paciente registrado con éxito', 'success');
      }

      // Reset form and close
      setNewPatient({
        full_name: '',
        dni: '',
        phone: '',
        email: '',
        insurance_provider: 'PARTICULAR',
        insurance_plan: '-',
        affiliate_number: ''
      });
      setShowCreatePatient(false);
    } catch (err) {
      console.error('Error saving patient:', err);
      addNotification('Error al registrar: ' + err.message, 'error');
    } finally {
      setIsSavingPatient(false);
    }
  };

  return createPortal(
    <div className="appointment-modal-overlay" style={{ zIndex: 100005 }} onClick={(e) => { e.stopPropagation(); if (onClose) onClose(); }}>
      <div className="appointment-modal-container" onClick={e => e.stopPropagation()}>
        <div className="appointment-modal-header">
          <div className="header-title">
            <CalendarIcon size={20} />
            <span>{formData.id ? 'MODIFICAR TURNO' : 'DATOS DEL TURNO'}</span>
          </div>
          <button className="btn-close-modal" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className="appointment-modal-tabs">
          <button 
            className={`modal-tab ${activeTab === 'datos' ? 'active' : ''}`}
            onClick={() => setActiveTab('datos')}
          >
            DATOS PRINCIPALES
          </button>
          <button 
            className={`modal-tab ${activeTab === 'historial' ? 'active' : ''}`}
            onClick={() => setActiveTab('historial')}
          >
            HISTORIAL DE TURNOS
          </button>
        </div>

        <div className="appointment-modal-body">
          <div className="form-section" style={{ display: 'block', width: '100%' }}>
            {conflictAppointment && (
              <div style={{
                background: 'rgba(255, 171, 0, 0.14)',
                border: '1px solid rgba(255, 171, 0, 0.4)',
                borderRadius: '10px',
                padding: '12px 16px',
                marginBottom: '16px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                color: '#ffb300',
                fontSize: '0.85rem'
              }}>
                <AlertTriangle size={24} style={{ flexShrink: 0, color: '#ffb300' }} />
                <div>
                  <strong style={{ color: '#ffd54f', display: 'block', marginBottom: '2px', fontSize: '0.9rem' }}>
                    ⚠️ ¡ALERTA DE SOBREPOSICIÓN DE TURNO!
                  </strong>
                  El paciente ya tiene un turno asignado la misma fecha (<strong>{conflictAppointment.appointment_date}</strong>) a las <strong>{conflictAppointment.appointment_time?.substring(0, 5)} hs</strong> con <strong>{conflictAppointment.professional_name}</strong> ({conflictAppointment.study_type || 'Consulta'}).
                </div>
              </div>
            )}
            <div className="form-row" style={{ width: '100%' }}>
              <div className="form-group full" style={{ width: '100%', flex: '1 1 100%' }}>
                <label>PACIENTE</label>
                <div className="patient-search-wrapper" style={{ width: '100%', flex: '1 1 100%' }}>
                    <div className="search-input-box">
                      <div className="search-icon-box">
                        {isSearching ? <Loader2 size={16} className="search-icon spinning" /> : <Search size={16} className="search-icon" />}
                      </div>
                      <input 
                        type="text" 
                        placeholder="ESCRIBA APELLIDO, NOMBRE O DNI..." 
                        value={patientSearch}
                        onChange={(e) => searchPatients(e.target.value)}
                        onFocus={() => patientSearch.length >= 2 && setShowResults(true)}
                      />
                      
                      {showResults && (
                        <div className="search-results-dropdown-modal">
                          {searchResults.length > 0 ? (
                            searchResults.map(p => (
                              <div 
                                key={p.id} 
                                className="search-result-item"
                                onClick={() => selectPatient(p)}
                              >
                                <div className="result-avatar"><User size={14} /></div>
                                <div className="result-info">
                                  <span className="result-name">{p.full_name}</span>
                                  <span className="result-dni">DNI: {p.dni || 'S/D'}</span>
                                </div>
                                <div className="result-insurance">{p.insurance_provider || 'PARTICULAR'}</div>
                              </div>
                            ))
                          ) : !isSearching && (
                            <div className="no-results-msg">
                              <AlertCircle size={14} />
                              <span>No se encontró el paciente. Puede cargarlo como nuevo.</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  <button 
                    type="button" 
                    className="btn-add-patient"
                    onClick={() => setShowCreatePatient(true)}
                    title="Registrar nuevo paciente"
                  >
                    <UserPlus size={18} />
                  </button>
                </div>
              </div>
            </div>

              {initialData?.requester_name && (
                <div className="form-row" style={{ width: '100%' }}>
                  <div className="form-group full" style={{ width: '100%', flex: '1 1 100%', background: 'rgba(0, 229, 255, 0.05)', padding: '10px 15px', borderRadius: '8px', borderLeft: '3px solid #00e5ff', display: 'flex', gap: '20px', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <User size={16} color="#00e5ff" />
                      <div>
                        <span style={{ display: 'block', fontSize: '0.7rem', color: '#00e5ff', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Solicitado por (Tercero)</span>
                        <span style={{ fontSize: '0.9rem', color: 'var(--text-bright)' }}>{initialData.requester_name}</span>
                      </div>
                    </div>
                    {initialData.requester_phone && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Phone size={16} color="#00e5ff" />
                        <div>
                          <span style={{ display: 'block', fontSize: '0.7rem', color: '#00e5ff', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Teléfono</span>
                          <span style={{ fontSize: '0.9rem', color: 'var(--text-bright)' }}>{initialData.requester_phone}</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

            <div className="form-row">
              <div className="form-group">
                <label>FECHA</label>
                <div className="input-icon-wrapper">
                  <CalendarIcon size={16} />
                  <input 
                    type="date" 
                    value={formData.date}
                    onChange={(e) => setFormData({...formData, date: e.target.value})}
                  />
                </div>
              </div>
              <div className="form-group">
                <label>HORA</label>
                <div className="input-icon-wrapper">
                  <Clock size={16} />
                  <input 
                    type="time" 
                    value={formData.time}
                    onChange={(e) => setFormData({...formData, time: e.target.value})}
                  />
                </div>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>OBRA SOCIAL</label>
                <input 
                  type="text" 
                  value={formData.insurance}
                  onChange={(e) => setFormData({...formData, insurance: e.target.value})}
                  placeholder="Ej: OSDE, Swiss Medical..."
                  className="modal-input"
                />
              </div>
              <div className="form-group">
                <label>PLAN</label>
                <input 
                  type="text" 
                  value={formData.plan}
                  onChange={(e) => setFormData({...formData, plan: e.target.value})}
                  placeholder="Ej: 210, 310..."
                  className="modal-input"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>TELÉFONO (RECORDATORIOS)</label>
                <input 
                  type="tel" 
                  value={formData.patient_phone || ''}
                  onChange={(e) => setFormData({...formData, patient_phone: e.target.value})}
                  placeholder="Ej: +54 9 11..."
                  className="modal-input"
                />
              </div>
              <div className="form-group">
                <label>CORREO ELECTRÓNICO</label>
                <input 
                  type="email" 
                  value={formData.patient_email || ''}
                  onChange={(e) => setFormData({...formData, patient_email: e.target.value})}
                  placeholder="Ej: paciente@email.com"
                  className="modal-input"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>ESPECIALIDAD</label>
                <div className="select-wrapper">
                  <select value={formData.specialty} disabled>
                    <option>{formData.specialty}</option>
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label>PROFESIONAL</label>
                <div className="select-wrapper">
                  <select 
                    value={formData.professional}
                    onChange={(e) => {
                      const profName = e.target.value;
                      const prof = professionalsList.find(p => p.full_name === profName);
                      setFormData({...formData, professional: profName, specialty: prof?.specialty || 'CONSULTA GENERAL'});
                    }}
                    disabled={activeStaff.type === 'Profesional' && !!staffName}
                  >
                    {(activeStaff.type === 'Profesional' && !!staffName) ? (
                      <option value={staffName}>{staffName}</option>
                    ) : (
                      <>
                        {professionalsList.filter(p => p.type === 'Profesional' && !p.is_hidden).map(p => (
                          <option key={p.id} value={p.full_name}>{p.full_name}</option>
                        ))}
                      </>
                    )}
                  </select>
                  <ChevronDown size={16} className="select-icon" />
                </div>
              </div>
            </div>

            

            <div style={{ width: "100%", display: "block", marginBottom: "1.5rem" }}>
              <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: "0.6rem" }}>
                <label style={{ fontSize: "0.7rem", fontWeight: 600, color: "#aaa", letterSpacing: "0.05em", paddingLeft: "0.25rem" }}>ARCHIVOS ADJUNTOS</label>
                <div style={{ display: 'flex', gap: '10px', flexDirection: 'column' }}>
                  {attachedFiles.map((f, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px', background: 'rgba(255,255,255,0.05)', padding: '5px 10px', borderRadius: '4px' }}>
                      <FileText size={14} />
                      <a href={f.url} target="_blank" rel="noreferrer" style={{ color: '#00e5ff', fontSize: '12px', flex: 1 }}>{f.name}</a>
                      <button type="button" onClick={() => setAttachedFiles(prev => prev.filter((_, idx) => idx !== i))} style={{ background: 'transparent', border: 'none', color: '#ff4444', cursor: 'pointer' }}><X size={14}/></button>
                    </div>
                  ))}
                  <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', background: 'rgba(0, 229, 255, 0.1)', border: '1px dashed #00e5ff', color: '#00e5ff', padding: '10px', borderRadius: '4px', cursor: 'pointer' }}>
                    {isUploading ? <Loader2 className="animate-spin" size={16} /> : <Upload size={16} />}
                    {isUploading ? 'SUBIENDO...' : 'SUBIR NUEVO ARCHIVO'}
                    <input type="file" style={{ display: 'none' }} onChange={handleFileUpload} disabled={isUploading} />
                  </label>
                </div>
              </div>
            </div>

            <div style={{ width: "100%", display: "block", marginBottom: "1.5rem" }}>
              <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: "0.6rem" }}>
                <label style={{ fontSize: "0.7rem", fontWeight: 600, color: "#aaa", letterSpacing: "0.05em", paddingLeft: "0.25rem" }}>OBSERVACIONES</label>
                <textarea 
                  placeholder="Ingrese notas internas del turno..."
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  style={{ width: "100%", minWidth: "100%" }}
                ></textarea>
              </div>
            </div>
          </div>
        </div>

        <div className="appointment-modal-footer">
          <button className="btn-cancel-modal" onClick={onClose}>
            CANCELAR
          </button>
          {!formData.id && (
            <button 
              className="btn-save-appointment" 
              style={{ 
                background: !!conflictAppointment ? 'rgba(255,255,255,0.05)' : 'rgba(59, 130, 246, 0.2)', 
                border: !!conflictAppointment ? '1px solid rgba(255,255,255,0.1)' : '1px solid var(--primary-cyan)', 
                color: !!conflictAppointment ? 'rgba(255,255,255,0.3)' : 'var(--primary-cyan)', 
                marginRight: '10px',
                cursor: !!conflictAppointment ? 'not-allowed' : 'pointer'
              }} 
              disabled={isSubmitting || !!conflictAppointment}
              title={!!conflictAppointment ? 'No se puede agendar por conflicto de horario con otro turno del paciente' : ''}
              onClick={async () => {
                if (isSubmitting || !!conflictAppointment) return;
                setIsSubmitting(true);
                try {
                  await onSave({...formData, attached_files: attachedFiles}, { keepOpen: true });
                  setFormData(prev => ({ ...prev, studyType: 'CONSULTA GENERAL', date: '', time: '' }));
                } catch (e) {
                  // error handled by parent
                } finally {
                  setIsSubmitting(false);
                }
              }}
            >
              <Plus size={16} style={{ display: 'inline', marginRight: '5px' }} />
              AGREGAR OTRO
            </button>
          )}
          <button 
            className="btn-save-appointment" 
            disabled={isSubmitting || !!conflictAppointment}
            title={!!conflictAppointment ? 'No se puede agendar por conflicto de horario con otro turno del paciente' : ''}
            style={{
              background: !!conflictAppointment ? 'rgba(255, 171, 0, 0.15)' : undefined,
              border: !!conflictAppointment ? '1px solid rgba(255, 171, 0, 0.3)' : undefined,
              color: !!conflictAppointment ? 'rgba(255, 255, 255, 0.4)' : undefined,
              cursor: !!conflictAppointment ? 'not-allowed' : 'pointer'
            }}
            onClick={async () => {
              if (isSubmitting || !!conflictAppointment) return;
              setIsSubmitting(true);
              try {
                await onSave({...formData, attached_files: attachedFiles});
              } finally {
                setIsSubmitting(false);
              }
            }}
          >
            {isSubmitting ? 'GUARDANDO...' : (formData.id ? 'GUARDAR CAMBIOS' : 'GUARDAR Y FINALIZAR')}
          </button>
        </div>

        {showCreatePatient && (
          <div className="new-patient-overlay">
            <div className="new-patient-header">
              <div className="header-title">
                <UserPlus size={20} />
                <span>NUEVO PACIENTE</span>
              </div>
              <button 
                type="button" 
                className="btn-close-modal" 
                onClick={() => setShowCreatePatient(false)}
              >
                <X size={20} />
              </button>
            </div>
            
            <form onSubmit={handleSaveNewPatient} className="new-patient-form">
              <div className="new-patient-body">
                <div className="form-row">
                  <div className="form-group full" style={{ position: "relative" }}>
                    <label>Nombre Completo *</label>
                    <input 
                      type="text" 
                      required 
                      placeholder="Ej: JUAN PEREZ"
                      value={newPatient.full_name}
                      onChange={(e) => {
                        const val = e.target.value.toUpperCase();
                        setNewPatient({...newPatient, full_name: val});
                        searchNewPatients(val);
                      }}
                      onFocus={(e) => { if(e.target.value.length >= 2) setShowNewPatientResults(true); }}
                      autoFocus
                    />
                    
                    {showNewPatientResults && (
                      <div className="search-results-dropdown-modal" style={{ top: '100%', zIndex: 100 }}>
                        {newPatientSearchResults.length > 0 ? (
                          newPatientSearchResults.map(p => (
                            <div 
                              key={p.id} 
                              className="search-result-item"
                              onClick={() => {
                                selectPatient(p);
                                setShowCreatePatient(false);
                                addNotification('Paciente existente seleccionado', 'success');
                              }}
                            >
                              <div className="result-avatar"><User size={14} /></div>
                              <div className="result-info">
                                <span className="result-name">{p.full_name}</span>
                                <span className="result-dni">DNI: {p.dni || 'S/D'}</span>
                              </div>
                              <div className="result-insurance">{p.insurance_provider || 'PARTICULAR'}</div>
                            </div>
                          ))
                        ) : (
                          <div className="no-results-msg" style={{ padding: '10px' }}>
                            <span style={{ fontSize: '0.8rem', color: '#888' }}>No se encontraron coincidencias. Puede continuar creando.</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>DNI / Documento *</label>
                    <input 
                      type="text" 
                      required
                      placeholder="Ej: 30123456"
                      value={newPatient.dni}
                      onChange={(e) => setNewPatient({...newPatient, dni: e.target.value})}
                    />
                  </div>
                  <div className="form-group">
                    <label>Teléfono *</label>
                    <input 
                      type="tel" 
                      required
                      placeholder="Ej: 1134567890"
                      value={newPatient.phone}
                      onChange={(e) => setNewPatient({...newPatient, phone: e.target.value})}
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group full">
                    <label>Email</label>
                    <input 
                      type="email" 
                      placeholder="ejemplo@correo.com"
                      value={newPatient.email}
                      onChange={(e) => setNewPatient({...newPatient, email: e.target.value})}
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Obra Social</label>
                    <input 
                      type="text" 
                      value={newPatient.insurance_provider}
                      onChange={(e) => setNewPatient({...newPatient, insurance_provider: e.target.value})}
                      placeholder="Ej: OSDE, Swiss Medical..."
                      className="modal-input"
                    />
                  </div>
                  <div className="form-group">
                    <label>Plan</label>
                    <input 
                      type="text" 
                      value={newPatient.insurance_plan}
                      onChange={(e) => setNewPatient({...newPatient, insurance_plan: e.target.value})}
                      placeholder="Ej: 210, 310..."
                      className="modal-input"
                    />
                  </div>

                  {newPatient.insurance_provider !== 'PARTICULAR' && (
                    <div className="form-group">
                      <label>Nro. Afiliado</label>
                      <input 
                        type="text" 
                        placeholder="Ej: 12345678-00"
                        value={newPatient.affiliate_number}
                        onChange={(e) => setNewPatient({...newPatient, affiliate_number: e.target.value})}
                      />
                    </div>
                  )}
                </div>
              </div>

              <div className="new-patient-footer">
                <button 
                  type="button" 
                  className="btn-cancel-modal" 
                  onClick={() => setShowCreatePatient(false)}
                >
                  CANCELAR
                </button>
                <button 
                  type="submit" 
                  className="btn-save-patient-action"
                  disabled={isSavingPatient}
                >
                  {isSavingPatient ? (
                    <>
                      <Loader2 size={16} className="spinning" style={{ marginRight: '0.5rem' }} />
                      GUARDANDO...
                    </>
                  ) : (
                    'GUARDAR Y SELECCIONAR'
                  )}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default AppointmentModal;
